import React, { createRef } from 'react';
import classnames from 'classnames';
import Cropper from 'cropperjs';
import { Wrapper, Loading } from '@/components';
import QueueAnim from 'rc-queue-anim';
import { Theme } from '@/sunzi';
import RotateVernier from '../rotate-vernier';
import 'cropperjs/dist/cropper.css';
import styles from './style.less';

interface CropperProps {
  source: string;
  maxWidth?: number;
  maxHeight?: number;
  width?: number;
  height?: number;
  aspectRatio?: number;
  data?: Cropper.Data;
  tip?: string;
  gtag: any;
  theme?: Theme;
  renderHeader: React.ReactNode;
  renderFooter?: React.ReactNode;
}

interface CropperState {
  loading: boolean;
  toastVisible: boolean;
}

class Component extends React.PureComponent<CropperProps, CropperState> {

  private _image: React.RefObject<HTMLImageElement> = createRef();
  private _vernier: React.RefObject<RotateVernier> = createRef();
  private _cropper?: Cropper;

  static defaultProps = {
    maxWidth: 1200,
    maxHeight: 1200,
  }

  constructor(props: CropperProps) {
    super(props);
    this.state = {
      loading: true,
      toastVisible: false,
    }
  }

  componentDidMount() {
    if (this._image.current) {
      const { aspectRatio, tip, width, height, data } = this.props;
      let _aspectRatio = aspectRatio;
      if (width && height)
        _aspectRatio = _aspectRatio || (width / height);
      this._cropper = new Cropper(this._image.current, {
        data,
        dragMode: 'move',
        background: false,
        viewMode: 1,
        aspectRatio: _aspectRatio,
        ready: () => {
          this.setState({
            loading: false
          });
          this._cropper?.getImageData().rotate || 0;
        }
      });
      if (tip) {
        setTimeout(() => {
          this.setState({
            toastVisible: true
          });
        }, 1000);
        setTimeout(() => {
          this.setState({
            toastVisible: false
          });
        }, 5000);
      }
    }
  }

  public onConfirm() : string | undefined {
    const { maxWidth, maxHeight, width, height } = this.props;
    if (this._cropper) {
      const _canvas = this._cropper.getCroppedCanvas({
        width,
        height,
        maxWidth,
        maxHeight,
      });
      const result = _canvas.toDataURL('image/jpeg');
      _canvas.width = _canvas.height = 0;
      return result;
    }
    return;
  }

  public getCropperData() {
    return this._cropper?.getData();
  }

  // 重置
  reset = () => {
    this._cropper?.reset();
    this._vernier.current?.reset();
  }

  // 放大
  handleZoomIn = () => {
    if (this._cropper) {
      this._cropper.zoom(0.1);
      this.props.gtag('event', 'cropper_footer_click', {
        'event_label': 'zoom_in'
      });
    }
  }

  // 缩小
  handleZoomOut = () => {
    if (this._cropper) {
      this._cropper.zoom(-0.1);
      this.props.gtag('event', 'cropper_footer_click', {
        'event_label': 'zoom_out'
      });
    }
  }

  // 逆时针旋转
  handleRotateLeft = () => {
    if (this._cropper) {
      this._cropper.rotate(-5);
      this.props.gtag('event', 'cropper_footer_click', {
        'event_label': 'rotate_left'
      });
    }
  }

  // 顺时针旋转
  handleRotateRight = () => {
    if (this._cropper) {
      this._cropper.rotate(5);
      this.props.gtag('event', 'cropper_footer_click', {
        'event_label': 'rotate_right'
      });
    }
  }

  // 旋转控件滚动条改变
  handleVernierChange = (rotate: number) => {
    this._cropper?.rotateTo(rotate);
  }

  render() {
    const { renderHeader, renderFooter, source, tip, theme } = this.props;
    const { loading, toastVisible } = this.state;

    return (
      <div className={classnames(styles.cropper, {
        [styles.cropperLight]: theme === Theme.light
      })}>
        {renderHeader}
        <div className={classnames(styles.cropperContent, {
          [styles.cropperContentHasFooter]: !!renderFooter
        })}>
          <QueueAnim animConfig={{ height: [54, 0] }} interval={50} leaveReverse>
            {toastVisible &&
              <div key="toast" className={styles.cropperContentToast}>{tip}</div>
            }
          </QueueAnim>
          <img ref={this._image} src={source} />
          {loading &&
            <Wrapper className={styles.loading}>
              <Loading />
            </Wrapper>
          }
        </div>
        <div className={styles.cropperVernier}>
          <RotateVernier
            ref={this._vernier}
            theme={theme}
            onZoomIn={this.handleZoomIn}
            onZoomOut={this.handleZoomOut}
            onRotateLeft={this.handleRotateLeft}
            onRotateRight={this.handleRotateRight}
            onVernierChange={this.handleVernierChange}
          />
        </div>
        {renderFooter}
      </div>
    );
  }
}

export default Component;
